import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { REGISTRATION_ENABLED } from '@/lib/utils';
import { parseLocalDate } from '@/utils/dateUtils';
import { addDays, format, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Trophy, Calendar, Users, Target, TrendingUp, TrendingDown, Minus, Crown, Medal, Award, Zap, ArrowUp, ArrowDown } from 'lucide-react';
import LeagueInfo from './LeagueInfo';
import WeeklyCalendar from './WeeklyCalendar';
import LeagueMatchCard from './LeagueMatchCard';
import LeagueMultiMatchCard from './LeagueMultiMatchCard';
import LeagueMatchCarousel from './LeagueMatchCarousel';
import CourtParticipantsList from './CourtParticipantsList';
import EncounterResultsSection from './EncounterResultsSection';
import LeagueRankingByEncounter from './LeagueRankingByEncounter';
import BracketTreeView from '../BracketTreeView';
import ParticipantCourtBooking from './ParticipantCourtBooking';
import { usePrizes } from '@/hooks/usePrizes';
import { CategoryPrizes } from '../PrizesDialog';
import { useUserRegistration } from '@/hooks/useUserRegistration';
import { useLeagueAttendance } from '@/hooks/useLeagueAttendance';
import { useLeagueMatches } from '@/hooks/useLeagueMatches';
import { useLeagueSchedule, calculateGameDateStatus, extractDateFromFormatted, EliminationPhaseSchedule } from '@/hooks/useLeagueSchedule';
import { EliminationScheduleConfig } from './WeeklyCalendar';
import { useLeagueRanking } from '@/hooks/useLeagueRanking';
import { useTennisChallengeEncounters } from '@/hooks/useTennisChallengeEncounters';
import { generateLeagueWeeks, calculateWeeksRemaining, formatTimeRange, formatTo12h, calculateWeekEndDate, interleaveRestWeeks } from '@/utils/leagueCalendarUtils';
interface LeagueProps {
  id: string;
  name: string;
  date: string;
  endDate: string;
  club: string;
  modality: 'round-robin' | 'escalera-piramidal' | 'sumatoria-juegos';
  gameType: 'singles' | 'doubles';
  sport: 'padel' | 'tennis' | 'pickleball';
  category: string;
  subcategory: string;
  partner?: string;
  currentStage: string;
  status: 'upcoming' | 'active' | 'finished';
  start_time?: string;
  end_time?: string;
  game_days?: string[];
  total_weeks?: number;
  matches_per_week?: number;
  organizer?: string;
  group_rules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
  additional_rules?: {
    timing?: {
      warmupMinutes?: number;
      restMinutes?: number;
      sideChangeSeconds?: number;
    };
    conduct?: {
      toleranceMinutes?: number;
    };
  };
  // Ladder league specific fields
  winnerType?: 'first-place' | 'elimination';
  challengeType?: 'simple' | 'double';
  eliminationPhase?: 'octavos' | 'cuartos' | 'semifinal' | 'final';
  eliminationConfig?: {
    singleDateSchedule?: {
      date?: string;
      startTime?: string;
      endTime?: string;
      courts?: number;
    };
    phaseSchedules?: Record<string, {
      date?: string;
      startTime?: string;
      endTime?: string;
    }>;
  };
  restWeeks?: number[];
}

interface LeagueEscaleraViewProps {
  league: LeagueProps;
  leagueMatches: any;
  onContactOrganizer?: () => void;
  onRegisterClick?: () => void;
  isUserRegistered?: boolean;
  registrationLoading?: boolean;
}

interface LadderPosition {
  position: number;
  name: string;
  partner?: string;
  weeklyPositions: number[];
  challengesWon: number;
  challengesLost: number;
  totalMatches: number;
  isCurrentUser?: boolean;
}

interface Challenge {
  id: string;
  challenger: string;
  challenged: string;
  week: number;
  date: string;
  time: string;
  court: string;
  result?: string;
  status: 'pending' | 'played' | 'scheduled';
}

const LeagueEscaleraView = ({ league, leagueMatches, onContactOrganizer, onRegisterClick, isUserRegistered, registrationLoading }: LeagueEscaleraViewProps) => {
  const [selectedCalendarWeek, setSelectedCalendarWeek] = useState<any>(null);
  const [selectedEncuentro, setSelectedEncuentro] = useState<number | null>(null);
  const [showBrackets, setShowBrackets] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<'regular' | 'elimination'>('regular');
  const [showAllCourts, setShowAllCourts] = useState(false);
  
  // Fetch user registration, prizes, attendance, and schedule
  const { registration } = useUserRegistration(league.id);
  const { prizes: dbPrizes } = usePrizes(league.id);
  const { uiAttendance, updateAttendance, isUpdating: isUpdatingAttendance } = useLeagueAttendance(league.id);
  const { gameDates, eliminationPhases, loading: scheduleLoading } = useLeagueSchedule(league.id);
  
  // Fetch real matches from database
  const { 
    userMatchForEncounter,
    userMatchesForEncounter,
    userTeam,
    opponentTeam,
    opponentAttendanceStatus, 
    isLoading: isLoadingMatches,
    setSelectedEncounter: setMatchSelectedEncounter,
    allScheduledMatches,
    allMatchesByEncounter
  } = useLeagueMatches(league.id, selectedEncuentro ?? 1);
  
  // Tennis singles: fetch challenge encounters
  const isTennisSingles = (league.sport.toLowerCase() === 'tennis' || league.sport.toLowerCase() === 'tenis') && league.gameType === 'singles';
  const { userEncountersByWeek, challengeTypeByWeek, positionsAboveByWeek, eliminationMatchesByPhase, isLoading: isLoadingChallenges } = useTennisChallengeEncounters(
    isTennisSingles ? league.id : '',
    isTennisSingles ? league.total_weeks : undefined,
    isTennisSingles ? league.eliminationPhase : undefined,
    isTennisSingles ? registration?.subcategory : undefined
  );
  
  // Note: useLeagueRanking hook is called after weeks are calculated (line ~235)
  
  // Structure prizes for PrizesDialog
  const isTennisSport = league.sport.toLowerCase() === 'tenis' || league.sport.toLowerCase() === 'tennis';
  
  const allCategoryPrizes: CategoryPrizes[] = dbPrizes.reduce((acc: CategoryPrizes[], prize) => {
    let mainCategory: string;
    let subcategoryFromPrize: string;
    
    if (isTennisSport) {
      // Tennis categories are "Masculino-A", "Masculino-B" etc.
      const parts = prize.category.split('-');
      mainCategory = parts[0]?.trim() || 'General';
      subcategoryFromPrize = parts[1]?.trim() || 'General';
    } else {
      // Padel: Extract subcategory from format "2a - parejas" or "3era"
      const subcategoryMatch = prize.category.match(/^(\d+[a-z]*)/i);
      subcategoryFromPrize = subcategoryMatch ? subcategoryMatch[1] : prize.category;
      mainCategory = registration?.category || league.subcategory?.split(' ')[0] || 'General';
    }
    
    const existingCategory = acc.find(p => 
      p.category === mainCategory && p.subcategory === subcategoryFromPrize
    );
    
    const prizeData = {
      type: prize.description.toLowerCase().includes('trofeo') ? 'trofeo' as const :
            prize.description.toLowerCase().includes('raqueta') || prize.description.toLowerCase().includes('pala') ? 'raqueta' as const :
            prize.description.toLowerCase().includes('$') || prize.description.toLowerCase().includes('€') || prize.amount ? 'dinero' as const :
            'certificado' as const,
      amount: prize.amount,
      description: prize.description
    };
    
    const isFirstPlace = prize.description.toLowerCase().includes('1er') || 
                         prize.description.toLowerCase().includes('primer');
    
    if (existingCategory) {
      if (isFirstPlace) {
        existingCategory.firstPlace.push(prizeData);
      } else {
        existingCategory.secondPlace = existingCategory.secondPlace || [];
        existingCategory.secondPlace.push(prizeData);
      }
    } else {
      acc.push({
        category: mainCategory,
        subcategory: subcategoryFromPrize,
        firstPlace: isFirstPlace ? [prizeData] : [],
        secondPlace: isFirstPlace ? [] : [prizeData]
      });
    }
    return acc;
  }, []);
  
  const userCategory = registration?.category && registration?.subcategory 
    ? (isTennisSport 
        ? registration.subcategory  // Tennis: subcategory already contains "Masculino A" which matches prize "Masculino" + "A"
        : `${registration.category} ${registration.subcategory}`)
    : undefined;
  
  // Debug: Log league data para verificar valores
  console.log('LeagueEscaleraView - League data:', {
    sport: league.sport,
    sportLower: league.sport.toLowerCase(),
    gameType: league.gameType,
    modality: league.modality,
    name: league.name
  });
  
  // Detectar si es pádel singles (formato especial de canchas)
  // Nota: Aceptamos ambas variantes ('pádel' con acento y 'padel' sin acento) porque normalizeSport() retorna sin acento
  const isPadelSingles = (league.sport.toLowerCase() === 'pádel' || league.sport.toLowerCase() === 'padel') && league.gameType === 'singles';
  
  console.log('LeagueEscaleraView - isPadelSingles:', isPadelSingles, {
    originalSport: league.sport,
    sportCheck: league.sport.toLowerCase() === 'pádel',
    gameTypeCheck: league.gameType === 'singles'
  });

  // Generate weeks dynamically from database (top-level to follow React hooks rules)
  const timeRange = formatTimeRange(league.start_time, league.end_time);
  
  const encounterWeeks = useMemo(() => {
    // Priority 1: Use game dates from tournament_schedules
    if (gameDates && gameDates.length > 0) {
      const scheduleWeeks = gameDates.map((gd, index) => {
        const status = calculateGameDateStatus(gd.date);
        const isTennis = league.sport.toLowerCase() === 'tennis' || league.sport.toLowerCase() === 'tenis';
        const fullDate = new Date(gd.date);
        return {
          number: index + 1,
          date: isTennis ? extractDateFromFormatted(gd.formattedDate) : gd.formattedDate,
          dateEnd: isTennis ? calculateWeekEndDate(fullDate) : undefined,
          fullDate,
          status,
          time: timeRange
        };
      });
      
      // If no current week found, mark first future week as current
      const hasCurrentWeek = scheduleWeeks.some(w => w.status === 'current');
      if (!hasCurrentWeek) {
        const firstFutureIndex = scheduleWeeks.findIndex(w => w.status === 'future');
        if (firstFutureIndex !== -1) {
          scheduleWeeks[firstFutureIndex].status = 'current';
        }
      }
      
      return scheduleWeeks;
    }
    
    // Priority 2: Generate dynamically from start_date and total_weeks
    return generateLeagueWeeks(
      league.date,
      league.total_weeks || 8,
      league.start_time,
      league.end_time,
      league.sport
    );
  }, [gameDates, league.date, league.total_weeks, league.start_time, league.end_time, timeRange]);

  // Interleave rest weeks into the calendar
  const weeks = useMemo(() => {
    return interleaveRestWeeks(encounterWeeks, league.restWeeks || [], league.sport);
  }, [encounterWeeks, league.restWeeks, league.sport]);
  
  const weeksRemaining = calculateWeeksRemaining(weeks);

  // Generate expanded week labels that include rest weeks with proper date shifting
  const expandedWeekLabels = useMemo(() => {
    const restWeeks = league.restWeeks || [];
    if (restWeeks.length === 0) {
      return weeks.map(w => w.date ? `${w.date}${w.dateEnd ? ' - ' + w.dateEnd : ''}` : '');
    }

    const totalDisplayWeeks = weeks.length + restWeeks.length;
    const result: string[] = [];

    // Use the first week's date as the base for ALL calculations
    const baseDate = weeks.length > 0 ? weeks[0].fullDate : null;
    if (!baseDate) {
      return weeks.map(w => w.date ? `${w.date}${w.dateEnd ? ' - ' + w.dateEnd : ''}` : '');
    }

    for (let i = 0; i < totalDisplayWeeks; i++) {
      const weekStart = addDays(baseDate, i * 7);
      const weekEnd = addDays(weekStart, 6);
      result.push(
        `${format(weekStart, 'd MMM', { locale: es })} - ${format(weekEnd, 'd MMM', { locale: es })}`
      );
    }

    return result;
  }, [weeks, league.restWeeks]);

  // Calculate week start dates for court booking date matching
  const weekStartDates = useMemo(() => {
    const restWeeksArr = league.restWeeks || [];
    const baseDate = weeks.length > 0 ? weeks[0].fullDate : null;
    if (!baseDate) return [];
    const totalChrono = weeks.filter(w => !w.number.toString().startsWith('elim')).length + restWeeksArr.length;
    return Array.from({ length: totalChrono }, (_, i) => addDays(baseDate, i * 7));
  }, [weeks, league.restWeeks]);

  // Build chrono week mapping: maps each chronological week to encounter or rest
  const chronoWeekMapping = useMemo(() => {
    const rw = league.restWeeks || [];
    if (rw.length === 0) return undefined; // No mapping needed if no rest weeks
    
    const regularWeekCount = weeks.filter(w => !w.number.toString().startsWith('elim')).length;
    const totalChrono = regularWeekCount + rw.length;
    const mapping: Array<{ chronoWeek: number; type: 'encounter' | 'rest'; encounterNumber?: number }> = [];
    let encounterCounter = 0;
    
    for (let i = 1; i <= totalChrono; i++) {
      if (rw.includes(i)) {
        mapping.push({ chronoWeek: i, type: 'rest' });
      } else {
        encounterCounter++;
        mapping.push({ chronoWeek: i, type: 'encounter', encounterNumber: encounterCounter });
      }
    }
    return mapping;
  }, [weeks, league.restWeeks]);

  // State for court booking week sync
  const [courtSelectedChronoWeek, setCourtSelectedChronoWeek] = useState<number | undefined>(undefined);

  const handleCourtWeekChange = useCallback((chronoWeek: number) => {
    setCourtSelectedChronoWeek(chronoWeek);
    if (chronoWeekMapping) {
      const entry = chronoWeekMapping.find(m => m.chronoWeek === chronoWeek);
      if (entry?.type === 'encounter' && entry.encounterNumber) {
        setSelectedEncuentro(entry.encounterNumber);
        setMatchSelectedEncounter(entry.encounterNumber);
      }
    } else {
      setSelectedEncuentro(chronoWeek);
      setMatchSelectedEncounter(chronoWeek);
    }
  }, [chronoWeekMapping, setMatchSelectedEncounter]);

  // Fetch rankings by encounter - pass weeks.length for calendar sync
  const {
    rankings,
    availableEncounters,
    getEncounterStatus,
    isLoading: isLoadingRanking
  } = useLeagueRanking(league.id, selectedEncuentro ?? 1, weeks.length, registration?.subcategory || undefined);
  if (isPadelSingles) {
    // Helper function to format time range (uses imported formatTo12h)
    const formatTimeRangeLocal = (startTime?: string, endTime?: string): string => {
      if (startTime && endTime) {
        return `${formatTo12h(startTime)} - ${formatTo12h(endTime)}`;
      }
      return "10:00 AM - 12:00 PM"; // Fallback
    };
    
    const totalMatches = (league.total_weeks || 8) * (league.matches_per_week || 3);
    const weeksPlayed = league.status === 'upcoming' ? 0 : league.status === 'finished' ? (league.total_weeks || 8) : 4;
    const matchesPlayed = weeksPlayed * (league.matches_per_week || 3);
    
    const leagueData = {
      leagueName: league.name,
      startDate: league.date,
      endDate: league.endDate,
      timeRange: formatTimeRangeLocal(league.start_time, league.end_time),
      totalMatches,
      matchesPlayed,
      sport: 'padel' as const,
      modalidad: 'singles' as const,
      club: league.club,
      category: registration?.category || league.category || '',
      subcategory: registration?.subcategory || league.subcategory || '',
      partner: "Compañeros Rotativos", // En pádel singles se juega con compañeros que rotan
      nextMatchDate: league.status === 'upcoming' ? "Por confirmar" : "Sábado 12 Abr",
      nextMatchTime: league.status === 'upcoming' ? "Por confirmar" : "10:00 AM",
      nextMatchCourt: league.status === 'upcoming' ? "Por confirmar" : "2",
      leagueType: 'escalera-piramidal' as const,
      firstPlacePrizes: [
        { type: 'trophy' as const },
        { type: 'racket' as const },
        { type: 'money' as const, amount: 400 }
      ],
      secondPlacePrizes: [
        { type: 'racket' as const }
      ],
      gameRules: [
        { label: 'Formato de Juego', value: '1 set a 8 juegos con Punto de Oro' },
        { label: 'Rotación', value: 'Pareja y rivales rotan cada partido' },
        { label: 'Objetivo Semanal', value: 'Ser el mejor de tu cancha para subir' },
        { label: 'Ganador Final', value: 'El jugador con mejor diferencia de juegos en la Cancha #1 es el campeón' }
      ]
    };


    // Datos de canchas por semana - Lógica correcta de escalera
    const weeklyCourts = {
      1: [
        {
          number: 1,
          level: "Cancha de Mayor Nivel",
          isTop: true,
          players: [
            { name: "Carlos Rivera", gamesFor: 8, gamesAgainst: 5, position: 1 },
            { name: "Diego López", gamesFor: 6, gamesAgainst: 8, position: 2 },
            { name: "Juan Pérez", gamesFor: 7, gamesAgainst: 8, position: 3 },
            { name: "Roberto Silva", gamesFor: 5, gamesAgainst: 7, position: 4 }
          ]
        },
        {
          number: 2,
          level: "Cancha Intermedia",
          isTop: false,
          players: [
            { name: "TÚ", gamesFor: 21, gamesAgainst: 21, position: 1, isCurrentUser: true },
            { name: "Fernando Mora", gamesFor: 4, gamesAgainst: 8, position: 2 },
            { name: "Miguel Castro", gamesFor: 3, gamesAgainst: 8, position: 3 },
            { name: "Andrés Jiménez", gamesFor: 2, gamesAgainst: 8, position: 4 }
          ]
        }
      ],
      2: [
        {
          number: 1,
          level: "Cancha de Mayor Nivel",
          isTop: true,
          players: [
            { name: "Carlos Rivera", gamesFor: 16, gamesAgainst: 9, position: 1 },
            { name: "Diego López", gamesFor: 14, gamesAgainst: 14, position: 2 },
            { name: "Juan Pérez", gamesFor: 14, gamesAgainst: 16, position: 3 },
            { name: "TÚ", gamesFor: 24, gamesAgainst: 17, position: 4, isCurrentUser: true }
          ]
        },
        {
          number: 2,
          level: "Cancha Intermedia",
          isTop: false,
          players: [
            { name: "Fernando Mora", gamesFor: 8, gamesAgainst: 16, position: 1 },
            { name: "Miguel Castro", gamesFor: 7, gamesAgainst: 16, position: 2 },
            { name: "Andrés Jiménez", gamesFor: 6, gamesAgainst: 16, position: 3 },
            { name: "Roberto Silva", gamesFor: 10, gamesAgainst: 14, position: 4 }
          ]
        }
      ],
      3: [
        {
          number: 1,
          level: "Cancha de Mayor Nivel",
          isTop: true,
          players: [
            { name: "Carlos Rivera", gamesFor: 22, gamesAgainst: 17, position: 1 },
            { name: "Diego López", gamesFor: 21, gamesAgainst: 22, position: 2 },
            { name: "Juan Pérez", gamesFor: 21, gamesAgainst: 24, position: 3 },
            { name: "Fernando Mora", gamesFor: 15, gamesAgainst: 24, position: 4 }
          ]
        },
        {
          number: 2,
          level: "Cancha Intermedia",
          isTop: false,
          players: [
            { name: "TÚ", gamesFor: 18, gamesAgainst: 24, position: 1, isCurrentUser: true },
            { name: "Miguel Castro", gamesFor: 14, gamesAgainst: 24, position: 2 },
            { name: "Andrés Jiménez", gamesFor: 12, gamesAgainst: 24, position: 3 },
            { name: "Roberto Silva", gamesFor: 18, gamesAgainst: 21, position: 4 }
          ]
        }
      ],
      4: [
        {
          number: 1,
          level: "Cancha de Mayor Nivel",
          isTop: true,
          players: [
            { name: "Carlos Rivera", gamesFor: 30, gamesAgainst: 20, position: 1 },
            { name: "Diego López", gamesFor: 27, gamesAgainst: 30, position: 2 },
            { name: "Juan Pérez", gamesFor: 27, gamesAgainst: 32, position: 3 },
            { name: "TÚ", gamesFor: 24, gamesAgainst: 14, position: 4, isCurrentUser: true }
          ]
        },
        {
          number: 2,
          level: "Cancha Intermedia",
          isTop: false,
          players: [
            { name: "Miguel Castro", gamesFor: 21, gamesAgainst: 32, position: 1 },
            { name: "Andrés Jiménez", gamesFor: 19, gamesAgainst: 32, position: 2 },
            { name: "Roberto Silva", gamesFor: 25, gamesAgainst: 29, position: 3 },
            { name: "Fernando Mora", gamesFor: 22, gamesAgainst: 32, position: 4 }
          ]
        }
      ],
      5: [
        {
          number: 1,
          level: "Cancha de Mayor Nivel",
          isTop: true,
          players: [
            { name: "Carlos Rivera", gamesFor: 38, gamesAgainst: 23, position: 1 },
            { name: "Diego López", gamesFor: 35, gamesAgainst: 37, position: 2 },
            { name: "Juan Pérez", gamesFor: 35, gamesAgainst: 40, position: 3 },
            { name: "Miguel Castro", gamesFor: 29, gamesAgainst: 40, position: 4 }
          ]
        },
        {
          number: 2,
          level: "Cancha Intermedia",
          isTop: false,
          players: [
            { name: "TÚ", gamesFor: 32, gamesAgainst: 22, position: 1, isCurrentUser: true },
            { name: "Andrés Jiménez", gamesFor: 27, gamesAgainst: 40, position: 2 },
            { name: "Roberto Silva", gamesFor: 33, gamesAgainst: 37, position: 3 },
            { name: "Fernando Mora", gamesFor: 30, gamesAgainst: 40, position: 4 }
          ]
        }
      ]
    };

    // Datos de partidos por semana
    const weeklyMatches = {
      1: [
        { matchNumber: 1, playerName: "TÚ", partnerName: "Carlos Rivera", opponent1: "Diego López", opponent2: "Juan Pérez", time: "10:00 AM", score: "8-5", isPlayed: true },
        { matchNumber: 2, playerName: "TÚ", partnerName: "Diego López", opponent1: "Carlos Rivera", opponent2: "Juan Pérez", time: "10:30 AM", score: "6-8", isPlayed: true },
        { matchNumber: 3, playerName: "TÚ", partnerName: "Juan Pérez", opponent1: "Carlos Rivera", opponent2: "Diego López", time: "11:00 AM", score: "7-8", isPlayed: true }
      ],
      2: [
        { matchNumber: 1, playerName: "TÚ", partnerName: "Carlos Rivera", opponent1: "Diego López", opponent2: "Juan Pérez", time: "10:00 AM", score: "8-4", isPlayed: true },
        { matchNumber: 2, playerName: "TÚ", partnerName: "Diego López", opponent1: "Carlos Rivera", opponent2: "Juan Pérez", time: "10:30 AM", score: "8-6", isPlayed: true },
        { matchNumber: 3, playerName: "TÚ", partnerName: "Juan Pérez", opponent1: "Carlos Rivera", opponent2: "Diego López", time: "11:00 AM", score: "8-7", isPlayed: true }
      ],
      3: [
        { matchNumber: 1, playerName: "TÚ", partnerName: "Carlos Rivera", opponent1: "Diego López", opponent2: "Juan Pérez", time: "10:00 AM", score: "6-8", isPlayed: true },
        { matchNumber: 2, playerName: "TÚ", partnerName: "Diego López", opponent1: "Carlos Rivera", opponent2: "Juan Pérez", time: "10:30 AM", score: "7-8", isPlayed: true },
        { matchNumber: 3, playerName: "TÚ", partnerName: "Juan Pérez", opponent1: "Carlos Rivera", opponent2: "Diego López", time: "11:00 AM", score: "5-8", isPlayed: true }
      ],
      4: [
        { matchNumber: 1, playerName: "TÚ", partnerName: "Carlos Rivera", opponent1: "Diego López", opponent2: "Juan Pérez", time: "10:00 AM", score: "8-3", isPlayed: true },
        { matchNumber: 2, playerName: "TÚ", partnerName: "Diego López", opponent1: "Carlos Rivera", opponent2: "Juan Pérez", time: "10:30 AM", score: "8-5", isPlayed: true },
        { matchNumber: 3, playerName: "TÚ", partnerName: "Juan Pérez", opponent1: "Carlos Rivera", opponent2: "Diego López", time: "11:00 AM", score: "8-6", isPlayed: true }
      ],
      5: [
        { matchNumber: 1, playerName: "TÚ", partnerName: "Carlos Rivera", opponent1: "Diego López", opponent2: "Juan Pérez", time: "10:00 AM", score: "8-6", isPlayed: true },
        { matchNumber: 2, playerName: "TÚ", partnerName: "Diego López", opponent1: "Carlos Rivera", opponent2: "Juan Pérez", time: "10:30 AM", score: "8-7", isPlayed: true },
        { matchNumber: 3, playerName: "TÚ", partnerName: "Juan Pérez", opponent1: "Carlos Rivera", opponent2: "Diego López", time: "11:00 AM", isPlayed: false }
      ]
    };

    // Datos de rendimiento y posición por semana - Corregido con lógica de escalera
    const weeklyPerformance = {
      1: { court: 2, position: 1, movement: "empezaste", previousCourt: 2, gamesFor: 21, gamesAgainst: 21, difference: 0 },
      2: { court: 1, position: 4, movement: "subiste", previousCourt: 2, gamesFor: 24, gamesAgainst: 17, difference: 7 },
      3: { court: 2, position: 1, movement: "bajaste", previousCourt: 1, gamesFor: 18, gamesAgainst: 24, difference: -6 },
      4: { court: 1, position: 4, movement: "subiste", previousCourt: 2, gamesFor: 24, gamesAgainst: 14, difference: 10 },
      5: { court: 2, position: 1, movement: "bajaste", previousCourt: 1, gamesFor: 32, gamesAgainst: 22, difference: 10 }
    };

    // Determinar qué partidos, rendimiento y canchas mostrar - Para ligas próximas, mostrar TBD
    const currentWeekNumber = selectedCalendarWeek?.number || (league.status === 'upcoming' ? 1 : league.status === 'finished' ? 8 : 5);
    
    // For upcoming leagues, show TBD data
    const displayMatches = league.status === 'upcoming' ? [
      { matchNumber: 1, playerName: "TÚ", partnerName: "Por confirmar", opponent1: "Por confirmar", opponent2: "Por confirmar", time: "Por confirmar", score: "", isPlayed: false },
      { matchNumber: 2, playerName: "TÚ", partnerName: "Por confirmar", opponent1: "Por confirmar", opponent2: "Por confirmar", time: "Por confirmar", score: "", isPlayed: false },
      { matchNumber: 3, playerName: "TÚ", partnerName: "Por confirmar", opponent1: "Por confirmar", opponent2: "Por confirmar", time: "Por confirmar", score: "", isPlayed: false }
    ] : (weeklyMatches[currentWeekNumber] || weeklyMatches[5]);
    
    const displayPerformance = league.status === 'upcoming' ? 
      { court: 2, position: 1, movement: "próximamente", previousCourt: 2, gamesFor: 0, gamesAgainst: 0, difference: 0 } :
      (weeklyPerformance[currentWeekNumber] || weeklyPerformance[5]);
    
    const displayCourts = league.status === 'upcoming' ? [
      {
        number: 1,
        level: "Cancha de Mayor Nivel",
        isTop: true,
        players: [
          { name: "TBD", gamesFor: 0, gamesAgainst: 0, position: 1 },
          { name: "TBD", gamesFor: 0, gamesAgainst: 0, position: 2 },
          { name: "TBD", gamesFor: 0, gamesAgainst: 0, position: 3 },
          { name: "TBD", gamesFor: 0, gamesAgainst: 0, position: 4 }
        ]
      },
      {
        number: 2,
        level: "Cancha Intermedia",
        isTop: false,
        players: [
          { name: "TBD", gamesFor: 0, gamesAgainst: 0, position: 1 },
          { name: "TBD", gamesFor: 0, gamesAgainst: 0, position: 2 },
          { name: "TBD", gamesFor: 0, gamesAgainst: 0, position: 3 },
          { name: "TBD", gamesFor: 0, gamesAgainst: 0, position: 4 }
        ]
      }
    ] : (weeklyCourts[currentWeekNumber] || weeklyCourts[5]);

  const handleWeekClick = (week: any) => {
    if (week.isRest) return; // Don't change selection on rest week click
    setSelectedCalendarWeek(week);
  };

  const handleAttendanceChange = (weekNumber: number, attendance: 'attending' | 'not-attending') => {
    console.log(`Semana ${weekNumber}: ${attendance === 'attending' ? 'Asistiré' : 'No asistiré'}`);
    updateAttendance(weekNumber, attendance);
  };

    return (
      <div className="space-y-8">
        {/* League Information */}
        <LeagueInfo {...leagueData} />

        {/* Weekly Calendar */}
        <WeeklyCalendar 
          weeks={weeks} 
          weeksRemaining={league.status === 'upcoming' ? (league.total_weeks || 8) : weeks.filter(w => w.status === 'future').length}
          onWeekClick={handleWeekClick}
          onAttendanceChange={handleAttendanceChange}
          initialAttendance={uiAttendance}
          isUpdatingAttendance={isUpdatingAttendance}
        hideAttendance={league.sport === 'tennis'}
        sport={league.sport}
        />

        {/* Court Booking - Tennis only */}
        {league.sport === 'tennis' && (
          <ParticipantCourtBooking
            eventId={league.id}
            selectedWeek={courtSelectedChronoWeek ?? selectedEncuentro ?? 1}
            matchDuration={90}
            totalWeeks={weeks.filter(w => !w.number.toString().startsWith('elim')).length}
            weekLabels={expandedWeekLabels}
            restWeeks={league.restWeeks}
            weekStartDates={weekStartDates}
            onWeekChange={handleCourtWeekChange}
          />
        )}

        {/* Encounter Results Section - Singles: show only user's matches for the encounter */}
        {(() => {
          const currentEncounter = selectedEncuentro || 1;
          // Use user's matches, fallback to all encounter matches if user has none
          const allEncounterMatches = (allMatchesByEncounter || {})[currentEncounter] || [];
          const encounterMatches = userMatchesForEncounter.length > 0 
            ? userMatchesForEncounter 
            : allEncounterMatches;
          const firstMatch = encounterMatches[0] || null;
          const singlesUserTeam = firstMatch ? { player1: firstMatch.team1_player1_name, player2: firstMatch.team1_player2_name } : null;
          const singlesOpponentTeam = firstMatch ? { player1: firstMatch.team2_player1_name, player2: firstMatch.team2_player2_name } : null;
          
          // Derive encounter status from actual matches
          const singlesEncounterStatus = encounterMatches.length === 0
            ? 'not_scheduled' as const
            : encounterMatches.every(m => m.status === 'completed')
              ? 'completed' as const
              : 'scheduled' as const;

          return (
            <EncounterResultsSection
              selectedEncounter={currentEncounter}
              onEncounterChange={(num) => {
                setSelectedEncuentro(num);
                setMatchSelectedEncounter(num);
                // Sync court booking: reverse lookup encounter → chrono week
                if (chronoWeekMapping) {
                  const entry = chronoWeekMapping.find(m => m.type === 'encounter' && m.encounterNumber === num);
                  if (entry) setCourtSelectedChronoWeek(entry.chronoWeek);
                } else {
                  setCourtSelectedChronoWeek(num);
                }
              }}
              availableEncounters={availableEncounters.length > 0 ? availableEncounters : [1]}
              encounterStatus={singlesEncounterStatus}
              userMatchForEncounter={firstMatch}
              userMatchesForEncounter={encounterMatches}
              userTeam={singlesUserTeam}
              opponentTeam={singlesOpponentTeam}
              weeks={weeks}
              league={league}
              userAttendanceStatus={
                uiAttendance[currentEncounter] === 'attending'
                  ? 'confirmed'
                  : uiAttendance[currentEncounter] === 'not-attending'
                    ? 'not_attending'
                    : 'pending'
              }
              opponentAttendanceStatus={opponentAttendanceStatus}
              isLoadingMatches={isLoadingMatches}
              onWeekChange={(weekNum) => {
                setSelectedEncuentro(weekNum);
                setMatchSelectedEncounter(weekNum);
              }}
              hasEliminationPhase={false}
              allScheduledMatches={allScheduledMatches}
              weekLabels={expandedWeekLabels}
              restWeeks={league.restWeeks}
              chronoWeekMapping={chronoWeekMapping}
              selectedChronoWeek={courtSelectedChronoWeek}
            />
          );
        })()}

        {/* Court Participants Attendance List */}
        <CourtParticipantsList
          eventId={league.id}
          encounterNumber={selectedEncuentro || 1}
        />

      </div>
    );
  }

  // Código existente para otras modalidades de escalera (doubles, etc.)
  // Mock data for ladder positions (formato tradicional)
  const ladderPositions: LadderPosition[] = league.status === 'upcoming' ? [
    {
      position: 1,
      name: "TBD",
      partner: league.gameType === 'doubles' ? "TBD" : undefined,
      weeklyPositions: [0, 0, 0, 0, 0],
      challengesWon: 0,
      challengesLost: 0,
      totalMatches: 0,
      isCurrentUser: false
    },
    {
      position: 2,
      name: "TBD",
      partner: league.gameType === 'doubles' ? "TBD" : undefined,
      weeklyPositions: [0, 0, 0, 0, 0],
      challengesWon: 0,
      challengesLost: 0,
      totalMatches: 0,
      isCurrentUser: false
    },
    {
      position: 3,
      name: "TBD",
      partner: league.gameType === 'doubles' ? "TBD" : undefined,
      weeklyPositions: [0, 0, 0, 0, 0],
      challengesWon: 0,
      challengesLost: 0,
      totalMatches: 0,
      isCurrentUser: false
    },
    {
      position: 4,
      name: "TBD",
      partner: league.gameType === 'doubles' ? "TBD" : undefined,
      weeklyPositions: [0, 0, 0, 0, 0],
      challengesWon: 0,
      challengesLost: 0,
      totalMatches: 0,
      isCurrentUser: false
    },
    {
      position: 5,
      name: "TBD",
      partner: league.gameType === 'doubles' ? "TBD" : undefined,
      weeklyPositions: [0, 0, 0, 0, 0],
      challengesWon: 0,
      challengesLost: 0,
      totalMatches: 0,
      isCurrentUser: false
    }
  ] : [
    {
      position: 1,
      name: "Carlos Mendoza",
      partner: league.gameType === 'doubles' ? "Ana López" : undefined,
      weeklyPositions: [3, 2, 1, 1, 1],
      challengesWon: 4,
      challengesLost: 1,
      totalMatches: 5,
      isCurrentUser: false
    },
    {
      position: 2,
      name: "Tu",
      partner: league.gameType === 'doubles' ? "María González" : undefined,
      weeklyPositions: [5, 4, 3, 2, 2],
      challengesWon: 3,
      challengesLost: 2,
      totalMatches: 5,
      isCurrentUser: true
    },
    {
      position: 3,
      name: "Diego Silva",
      partner: league.gameType === 'doubles' ? "Laura Ruiz" : undefined,
      weeklyPositions: [2, 3, 4, 3, 3],
      challengesWon: 2,
      challengesLost: 3,
      totalMatches: 5,
      isCurrentUser: false
    },
    {
      position: 4,
      name: "Roberto Fernández",
      partner: league.gameType === 'doubles' ? "Carmen Soto" : undefined,
      weeklyPositions: [1, 1, 2, 4, 4],
      challengesWon: 3,
      challengesLost: 2,
      totalMatches: 5,
      isCurrentUser: false
    },
    {
      position: 5,
      name: "Pedro Martín",
      partner: league.gameType === 'doubles' ? "Isabel García" : undefined,
      weeklyPositions: [4, 5, 5, 5, 5],
      challengesWon: 1,
      challengesLost: 4,
      totalMatches: 5,
      isCurrentUser: false
    }
  ];


   // Generar semanas de eliminación si winnerType === 'elimination' AND es escalera
   const canShowEliminationInEncounters = 
     league.winnerType === 'elimination' && 
    league.modality === 'escalera-piramidal';
  
  const hasEliminationPhase = league.winnerType === 'elimination';
  
  const eliminationWeeks = useMemo(() => {
    if (!hasEliminationPhase || !league.eliminationConfig) {
      return [];
    }
    
    const config = league.eliminationConfig;
    const phases: Array<{ number: number; date: string; status: 'past' | 'current' | 'future'; time: string; phase: string }> = [];
    
    // If there are phaseSchedules, use them
    const restWeekShiftDays = (league.restWeeks?.length || 0) * 7;
    
    if (config.phaseSchedules && Object.keys(config.phaseSchedules).length > 0) {
      const phaseOrder = ['octavos', 'cuartos', 'semifinal', 'final'];
      const phaseLabels: Record<string, string> = {
        'octavos': 'Octavos',
        'cuartos': 'Cuartos',
        'semifinal': 'Semifinales',
        'final': 'Final'
      };
      
      let weekNumber = weeks.length + 1;
      
      phaseOrder.forEach(phaseName => {
        const schedule = config.phaseSchedules?.[phaseName];
        if (schedule && schedule.date) {
          const originalDate = new Date(schedule.date);
          const phaseDate = addDays(originalDate, restWeekShiftDays);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          phaseDate.setHours(0, 0, 0, 0);
          
          let status: 'past' | 'current' | 'future' = 'future';
          if (phaseDate < today) status = 'past';
          else if (phaseDate.getTime() === today.getTime()) status = 'current';
          
          phases.push({
            number: weekNumber++,
            date: phaseDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
            status,
            time: schedule.startTime && schedule.endTime 
              ? `${schedule.startTime.slice(0, 5)} - ${schedule.endTime.slice(0, 5)}`
              : '18:00 - 20:00',
            phase: phaseLabels[phaseName] || phaseName
          });
        }
      });
    }
    // If there's a singleDateSchedule, generate one card per elimination phase
    else if (config.singleDateSchedule && config.singleDateSchedule.date) {
      const sched = config.singleDateSchedule;
      const originalDate = new Date(sched.date);
      const phaseDate = addDays(originalDate, restWeekShiftDays);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      phaseDate.setHours(0, 0, 0, 0);
      
      let status: 'past' | 'current' | 'future' = 'future';
      if (phaseDate < today) status = 'past';
      else if (phaseDate.getTime() === today.getTime()) status = 'current';

      const formattedDate = phaseDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
      const formattedTime = sched.startTime && sched.endTime 
        ? `${sched.startTime.slice(0, 5)} - ${sched.endTime.slice(0, 5)}`
        : '18:00 - 20:00';

      // Determine phase structure based on eliminationPhase
      const getPhaseLabels = (): string[] => {
        switch (league.eliminationPhase) {
          case 'octavos': return ['Octavos', 'Cuartos', 'Semifinales', 'Final'];
          case 'cuartos': return ['Cuartos', 'Semifinales', 'Final'];
          case 'semifinal': return ['Semifinales', 'Final'];
          case 'final': return ['Final'];
          default: return ['Semifinales', 'Final'];
        }
      };

      const phaseLabels = getPhaseLabels();
      phaseLabels.forEach((phaseLabel, i) => {
        phases.push({
          number: weeks.length + 1 + i,
          date: formattedDate,
          status,
          time: formattedTime,
          phase: phaseLabel
        });
      });
    }
    
    return phases;
  }, [hasEliminationPhase, league.eliminationConfig, weeks.length, league.restWeeks]);

  // Prepare elimination schedule config for the calendar
  const eliminationScheduleConfig: EliminationScheduleConfig | undefined = useMemo(() => {
    const config = league.eliminationConfig;
    
    const restWeekShiftDays = (league.restWeeks?.length || 0) * 7;
    
    // Priority 1: Single date schedule from ladderConfig
    if (config?.singleDateSchedule?.date) {
      const scheduleDate = addDays(new Date(config.singleDateSchedule.date), restWeekShiftDays);
      return {
        isSingleDate: true,
        singleDateInfo: {
          date: scheduleDate.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }),
          startTime: formatTo12h(config.singleDateSchedule.startTime || '18:00'),
          endTime: formatTo12h(config.singleDateSchedule.endTime || '21:00')
        }
      };
    }
    
    // Priority 2: Phase schedules from tournament_schedules table
    if (Object.keys(eliminationPhases).length > 0) {
      const phaseLabels: Record<string, string> = {
        'semifinal': 'Semifinales',
        'final': 'Final',
        'cuartos': 'Cuartos',
        'octavos': 'Octavos'
      };
      
      const formattedPhases: Record<string, { date: string; time: string }> = {};
      Object.entries(eliminationPhases).forEach(([phase, schedule]) => {
        const phaseDate = new Date(schedule.date);
        formattedPhases[phaseLabels[phase] || phase] = {
          date: phaseDate.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }),
          time: formatTo12h(schedule.time || schedule.startTime || '18:00')
        };
      });
      
      return {
        isSingleDate: false,
        phaseSchedules: formattedPhases
      };
    }
    
    // Priority 3: Phase schedules from ladderConfig
    if (config?.phaseSchedules && Object.keys(config.phaseSchedules).length > 0) {
      const phaseLabels: Record<string, string> = {
        'semifinal': 'Semifinales',
        'final': 'Final',
        'cuartos': 'Cuartos',
        'octavos': 'Octavos'
      };
      
      const formattedPhases: Record<string, { date: string; time: string }> = {};
      Object.entries(config.phaseSchedules).forEach(([phase, schedule]) => {
        if (schedule.date) {
          const phaseDate = addDays(new Date(schedule.date), restWeekShiftDays);
          formattedPhases[phaseLabels[phase] || phase] = {
            date: phaseDate.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }),
            time: formatTo12h(schedule.startTime || '18:00')
          };
        }
      });
      
      if (Object.keys(formattedPhases).length > 0) {
        return {
          isSingleDate: false,
          phaseSchedules: formattedPhases
        };
      }
    }
    
    return undefined;
  }, [league.eliminationConfig, eliminationPhases, league.restWeeks]);

  // Dynamic game rules based on ladder configuration
  const isDoubles = league.gameType === 'doubles';
  const challengeType = league.challengeType || 'simple';
  const challengeText = isDoubles
    ? "Las parejas por cancha se retan entre sí"
    : (challengeType === 'simple' 
      ? "Reta 1 posición arriba"
      : "Reta 1 o 2 posiciones arriba");

  const gameRules: Array<{ label: string; value: string; icon?: 'target' | 'trophy' | 'bracket' | 'calendar' }> = [
    { 
      label: "Formato de Retos", 
      value: challengeText,
      icon: 'target'
    },
    { 
      label: "Resultado", 
      value: isDoubles ? "Ganador sube y perdedor baja de cancha" : "Ganador toma la posición más alta",
      icon: 'trophy'
    }
  ];

  // Add "Encuentros" rule for doubles (Rey de la Cancha)
  if (isDoubles) {
    gameRules.push({
      label: "Encuentros",
      value: "Siempre se juegan encuentros entre parejas",
      icon: 'calendar'
    });
  }

  // Only add Playoff rule if winnerType is 'elimination'
  if (league.winnerType === 'elimination') {
    gameRules.push({ 
      label: "Playoff", 
      value: "Top 4 clasifican a eliminatorias finales",
      icon: 'bracket'
    });
  }

  const getMatchSectionTitle = () => {
    const effectiveWeekNum = selectedEncuentro || 5;
    const currentWeek = weeks.find(w => w.number === effectiveWeekNum);
    if (!currentWeek || currentWeek.status === 'past') {
      return `Resultado Semana ${effectiveWeekNum}`;
    }
    return `Tu Reto - Semana ${effectiveWeekNum}`;
  };

  // Helper to get user attendance status for current encounter
  const getUserAttendanceForEncounter = (): 'confirmed' | 'not_attending' | 'pending' => {
    const encounterNumber = selectedEncuentro || 1;
    const status = uiAttendance[encounterNumber];
    if (status === 'attending') return 'confirmed';
    if (status === 'not-attending') return 'not_attending';
    return 'pending';
  };

  const getMatchContent = () => {
    const effectiveWeekNum = selectedEncuentro || 1;
    const currentWeek = weeks.find(w => w.number === effectiveWeekNum);

    // PRIORITY 1: If we have MULTIPLE matches for this encounter, show carousel
    if (userMatchesForEncounter && userMatchesForEncounter.length > 1) {
      return (
        <LeagueMatchCarousel
          matches={userMatchesForEncounter}
          weeks={weeks}
          selectedWeek={effectiveWeekNum}
          onWeekChange={(weekNum) => {
            setSelectedEncuentro(weekNum);
            setMatchSelectedEncounter(weekNum);
          }}
          userAttendanceStatus={getUserAttendanceForEncounter()}
          opponentAttendanceStatus={opponentAttendanceStatus}
          isLoading={isLoadingMatches}
        />
      );
    }

    // PRIORITY 2: If we have a single match from the database, use it
    if (userMatchForEncounter && userTeam && opponentTeam) {
      const isPlayed = userMatchForEncounter.status === 'completed';
      
      // Format date from scheduled_date
      const matchDate = userMatchForEncounter.scheduled_date 
        ? parseLocalDate(userMatchForEncounter.scheduled_date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })
        : currentWeek?.date || "";
      
      // Format time from scheduled_time  
      const matchTime = userMatchForEncounter.scheduled_time 
        ? userMatchForEncounter.scheduled_time.slice(0, 5)
        : currentWeek?.time || "18:00 - 20:00";
      
      return (
        <LeagueMatchCard
          playerName={userTeam.player1}
          partnerName={userTeam.player2 || undefined}
          opponent1={opponentTeam.player1}
          opponent2={opponentTeam.player2 || undefined}
          date={matchDate}
          time={matchTime}
          court={`Cancha ${userMatchForEncounter.court_number || "1"}`}
          modalidad={league.gameType}
          sport={league.sport}
          title={isPlayed ? "Reto Completado" : "Tu Reto"}
          isPlayed={isPlayed}
          opponentAttendanceStatus={opponentAttendanceStatus}
          userAttendanceStatus={getUserAttendanceForEncounter()}
          isLoadingMatch={isLoadingMatches}
        />
      );
    }

    // PRIORITY 2: For upcoming leagues with no match data in DB, show TBD content
    if (league.status === 'upcoming') {
      return (
        <LeagueMatchCard
          playerName="TÚ"
          partnerName={league.gameType === 'doubles' ? (league.partner || "Por confirmar") : undefined}
          opponent1="Por confirmar"
          opponent2={league.gameType === 'doubles' ? "Por confirmar" : undefined}
          date="Por confirmar"
          time="Por confirmar"
          court="Por confirmar"
          modalidad={league.gameType}
          sport={league.sport}
          title="Próximos Retos"
          isPlayed={false}
        />
      );
    }
    
    // Fallback to mock data
    return (
      <LeagueMatchCard
        playerName="Tu"
        partnerName={league.gameType === 'doubles' ? league.partner : undefined}
        opponent1="Carlos Mendoza"
        opponent2={league.gameType === 'doubles' ? "Ana López" : undefined}
        date={currentWeek?.date || "13 Jul"}
        time="18:00 - 20:00"
        court="1"
        modalidad={league.gameType}
        sport={league.sport}
        title={currentWeek?.status === 'past' ? "Reto Ganado - Subiste a #2" : "Reto por Posición #1"}
        isPlayed={currentWeek?.status === 'past'}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* League Info */}
      <LeagueInfo
        leagueName={league.name}
        startDate={league.date}
        endDate={league.endDate}
        timeRange="18:00 - 20:00"
        totalMatches={(league.total_weeks || weeks.length) * (league.matches_per_week || 3)}
        matchesPlayed={league.status === 'upcoming' ? 0 : (weeks.filter(w => w.status === 'past').length * (league.matches_per_week || 3))}
        sport={league.sport}
        modalidad={league.gameType}
        partner={registration?.partner_name || league.partner || (league.gameType === 'doubles' ? "Compañero Asignado" : "")}
        club={league.club}
        category={registration?.category || league.category}
        subcategory={registration?.subcategory || league.subcategory}
        leagueType="escalera-piramidal"
        allCategoryPrizes={allCategoryPrizes}
        userCategory={userCategory}
        gameRules={gameRules}
        onContactOrganizer={onContactOrganizer}
        groupRules={league.group_rules}
        additionalRules={league.additional_rules}
        gameDays={league.game_days}
        startTime={league.start_time}
        endTime={league.end_time}
        organizerName={league.organizer}
      />

      {/* Weekly Calendar with Phase Toggle */}
      <WeeklyCalendar
        weeks={weeks}
        weeksRemaining={weeksRemaining}
        onWeekClick={(week) => setSelectedEncuentro(week.number)}
        onAttendanceChange={(weekNumber, attendance) => updateAttendance(weekNumber, attendance)}
        showPhaseToggle={hasEliminationPhase}
        currentPhase={currentPhase}
        onPhaseChange={setCurrentPhase}
        eliminationWeeks={eliminationWeeks}
        initialAttendance={uiAttendance}
        isUpdatingAttendance={isUpdatingAttendance}
        eliminationPhase={league.eliminationPhase}
        eliminationScheduleConfig={eliminationScheduleConfig}
        hideAttendance={league.sport === 'tennis'}
        sport={league.sport}
      />

      {/* Court Booking - Tennis only */}
      {league.sport === 'tennis' && (
        <ParticipantCourtBooking
          eventId={league.id}
          selectedWeek={courtSelectedChronoWeek ?? selectedEncuentro ?? 1}
          matchDuration={90}
          totalWeeks={weeks.filter(w => !w.number.toString().startsWith('elim')).length}
          weekLabels={expandedWeekLabels}
          restWeeks={league.restWeeks}
          weekStartDates={weekStartDates}
          onWeekChange={handleCourtWeekChange}
        />
      )}

      {/* Registration Button - Solo si NO está inscrito */}
      {!isUserRegistered && onRegisterClick && (
        <div className="px-0">
          <Button
            onClick={REGISTRATION_ENABLED ? onRegisterClick : undefined}
            disabled={registrationLoading || !REGISTRATION_ENABLED}
            className={`w-full h-16 text-lg font-bold shadow-lg hover:shadow-xl transition-all ${
              !REGISTRATION_ENABLED
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            {registrationLoading ? 'Cargando...' : '¡Inscríbeme!'}
          </Button>
        </div>
      )}

      {/* Encounter Results Section */}
      <EncounterResultsSection
        selectedEncounter={selectedEncuentro || 1}
        onEncounterChange={(num) => {
          setSelectedEncuentro(num);
          setMatchSelectedEncounter(num);
          // Sync court booking: reverse lookup encounter → chrono week
          if (chronoWeekMapping) {
            const entry = chronoWeekMapping.find(m => m.type === 'encounter' && m.encounterNumber === num);
            if (entry) setCourtSelectedChronoWeek(entry.chronoWeek);
          } else {
            setCourtSelectedChronoWeek(num);
          }
        }}
        availableEncounters={availableEncounters.length > 0 ? availableEncounters : [1]}
        encounterStatus={getEncounterStatus(selectedEncuentro || 1)}
        userMatchForEncounter={userMatchForEncounter}
        userMatchesForEncounter={userMatchesForEncounter}
        userTeam={userTeam}
        opponentTeam={opponentTeam}
        weeks={weeks}
        league={league}
        userAttendanceStatus={getUserAttendanceForEncounter()}
        opponentAttendanceStatus={opponentAttendanceStatus}
        isLoadingMatches={isTennisSingles ? isLoadingChallenges : isLoadingMatches}
        onWeekChange={(weekNum) => {
          setSelectedEncuentro(weekNum);
          setMatchSelectedEncounter(weekNum);
        }}
        eliminationPhase={league.eliminationPhase}
        hasEliminationPhase={canShowEliminationInEncounters}
        allScheduledMatches={allScheduledMatches}
        isTennisSingles={isTennisSingles}
        tennisChallengeEncounter={isTennisSingles ? (userEncountersByWeek[selectedEncuentro || 1] || null) : undefined}
        challengeType={isTennisSingles ? challengeTypeByWeek[selectedEncuentro || 1] : undefined}
        challengePositionsAbove={isTennisSingles ? positionsAboveByWeek[selectedEncuentro || 1] : undefined}
        tennisSinglesEliminationMatches={isTennisSingles ? eliminationMatchesByPhase : undefined}
        weekLabels={expandedWeekLabels}
        restWeeks={league.restWeeks}
        chronoWeekMapping={chronoWeekMapping}
        selectedChronoWeek={courtSelectedChronoWeek}
      />

      {/* Ranking by Encounter */}
      <LeagueRankingByEncounter
        rankings={rankings}
        selectedEncounter={selectedEncuentro || 0}
        onEncounterChange={(num) => {
          setSelectedEncuentro(num || 1);
          setMatchSelectedEncounter(num || 1);
          // Sync court booking: reverse lookup encounter → chrono week
          const effectiveNum = num || 1;
          if (chronoWeekMapping) {
            const entry = chronoWeekMapping.find(m => m.type === 'encounter' && m.encounterNumber === effectiveNum);
            if (entry) setCourtSelectedChronoWeek(entry.chronoWeek);
          } else {
            setCourtSelectedChronoWeek(effectiveNum);
          }
        }}
        availableEncounters={availableEncounters.length > 0 ? availableEncounters : [1]}
        isLoading={isLoadingRanking}
        gameType={league.gameType}
        onShowBrackets={() => setShowBrackets(true)}
      />

      {/* Playoffs Modal */}
      {showBrackets && (
        <Dialog open={showBrackets} onOpenChange={setShowBrackets}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Playoffs - Top 4
              </DialogTitle>
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800 font-medium">
                  🏆 Los 4 primeros clasificados compiten en eliminatorias directas
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Semifinales: 1° vs 4°, 2° vs 3° • Final: Ganadores semifinales
                </p>
              </div>
            </DialogHeader>
            <BracketTreeView 
              bracketData={{
                octavos: [],
                cuartos: [],
                semifinales: [
                  {
                    id: 1,
                    player1: "Carlos Mendoza/Ana López",
                    player2: "Roberto Fernández/Carmen Soto",
                    date: "20 Jul",
                    time: "19:00",
                    court: "1",
                    result: "6-3, 6-2",
                    winner: "Carlos Mendoza",
                    sets: {
                      player1: [6, 6],
                      player2: [3, 2]
                    }
                  },
                  {
                    id: 2,
                    player1: "Tu/María González",
                    player2: "Diego Silva/Laura Ruiz",
                    date: "20 Jul",
                    time: "20:00",
                    court: "2",
                    result: "4-6, 6-4, 10-8",
                    winner: "Tu",
                    sets: {
                      player1: [4, 6, 10],
                      player2: [6, 4, 8]
                    }
                  }
                ],
                final: [
                  {
                    id: 3,
                    player1: "Carlos Mendoza/Ana López",
                    player2: "Tu/María González",
                    date: "27 Jul",
                    time: "19:00",
                    court: "1"
                  }
                ]
              }}
              sport={league.sport}
              gameType={league.gameType}
              showBackButton={false}
              modality="elimination"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default LeagueEscaleraView;